import { GetInSplitLayout } from './get-in-split-layout'
import { FormFieldError } from 'src/component/ui/form-field-error'
import { OverlaySpinner } from '../../util/spinner/overlay-spinner'
import { InputOTP, InputOTPGroup, InputOTPSlot } from 'src/component/ui/input-otp'
import { useResendPasswordlessCode } from '../../../../hook/use-resend-passwordless-code'
import { useVerifyPasswordlessLogin } from '../../../../hook/use-verify-passwordless-login'
import { yupResolver } from '@hookform/resolvers/yup'
import { REGEXP_ONLY_DIGITS } from 'input-otp'
import React, { useCallback, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { useParams } from 'react-router-dom'

const OTP_CODE_REGEX = /^\d{6}$/

const otpSchema = yup
  .object({
    code: yup.string().matches(OTP_CODE_REGEX, 'Please enter a valid 6-digit code').required('Code is required'),
  })
  .required()

type FormValues = yup.InferType<typeof otpSchema>

export const GetInOtpStep = (): React.ReactElement => {
  const { email } = useParams<{ email: string }>()

  const { control, handleSubmit, reset, watch } = useForm<FormValues>({
    resolver: yupResolver(otpSchema),
    defaultValues: { code: '' },
    mode: 'onTouched',
  })

  const codeValue = watch('code')

  const { verifyPasswordlessLogin, isLoading: isVerifyLoading } = useVerifyPasswordlessLogin({
    onError: () => reset({ code: '' }),
  })

  const { resendPasswordlessCode, isLoading: isResendLoading } = useResendPasswordlessCode({
    onError: () => reset({ code: '' }),
    onSuccess: () => reset({ code: '' }),
  })

  const isLoading = isVerifyLoading || isResendLoading

  const onSubmit = useCallback(
    (data: FormValues): void => {
      verifyPasswordlessLogin({ email: email ?? '', code: data.code })
    },
    [email, verifyPasswordlessLogin],
  )

  const isCodeComplete = OTP_CODE_REGEX.test(codeValue ?? '')

  useEffect(() => {
    if (isCodeComplete && !isLoading) {
      handleSubmit(onSubmit)()
    }
  }, [isCodeComplete, isLoading, handleSubmit, onSubmit])

  const handleResendCode = useCallback((): void => {
    resendPasswordlessCode({ email: email ?? '' })
  }, [email, resendPasswordlessCode])

  return (
    <GetInSplitLayout>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="relative flex-1 flex flex-col items-center justify-center w-full max-w-[541px] mx-auto"
      >
        <div className="flex flex-col items-center gap-5 lg:gap-[clamp(14px,2.2dvh,20px)] w-full">
          <h1 className="font-evogria text-[36px] leading-[44px] text-center">
            ENTER YOUR <span className="font-evogria text-primary">6-DIGIT CODE</span>
          </h1>
          <div className="flex flex-col items-center gap-4 w-full">
            <Controller
              control={control}
              name="code"
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <>
                  <InputOTP
                    maxLength={6}
                    pattern={REGEXP_ONLY_DIGITS}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    className="justify-center"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} aria-invalid={!!error} hideCaret />
                      <InputOTPSlot index={1} aria-invalid={!!error} hideCaret />
                      <InputOTPSlot index={2} aria-invalid={!!error} hideCaret />
                      <InputOTPSlot index={3} aria-invalid={!!error} hideCaret />
                      <InputOTPSlot index={4} aria-invalid={!!error} hideCaret />
                      <InputOTPSlot index={5} aria-invalid={!!error} hideCaret />
                    </InputOTPGroup>
                  </InputOTP>
                  <FormFieldError className="text-error-2">{error?.message}</FormFieldError>
                </>
              )}
            />
          </div>
          <p className="font-roboto-condensed text-base leading-6 text-ivory tracking-[0.5px] text-center mb-5">
            Please enter the 6-digit code on the email sent to <span className="font-roboto-condensed-medium">{email}</span>
          </p>
          <button
            type="button"
            onClick={handleResendCode}
            disabled={isLoading}
            className="font-roboto-condensed-medium text-base text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Resend Email
          </button>
        </div>
        {isLoading && <OverlaySpinner />}
      </form>
    </GetInSplitLayout>
  )
}
